/* eslint-disable react/prop-types */
import { Box, Flex, Spinner, Text } from '@chakra-ui/react';
import React, { useMemo } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { formatCurrency } from 'utils/CommonFunction';
import { MerchantStationRevenueColor } from 'utils/StaticData';

// RAT-1804: the chart used to render one stacked <Bar> series per station across
// ALL merchants (X-axis) -> series x categories empty SVG <g> layers, e.g. 316
// stations x 270 merchants ~ 86k nodes locally and hundreds of thousands on
// production, which froze the main thread on dashboard load. We cap both axes:
// at most MAX_MERCHANT_BARS merchant bars and MAX_STATION_SERIES station series
// (the rest folded into a single "Others" segment), which keeps the node count
// bounded (~600) regardless of how many merchants/stations the API returns.
const MAX_MERCHANT_BARS = 15;
const MAX_STATION_SERIES = 40;
const OTHERS_KEY = '__others';
const OTHERS_LABEL = 'Others';

const COLORS = MerchantStationRevenueColor;
const colorAt = (index) => COLORS[index % COLORS.length];

const sumBy = (list, pick) =>
  list.reduce((sum, item) => sum + (pick(item) || 0), 0);

// Build a bounded chart model from the (unbounded) per-merchant/per-station data.
// Returns everything the render needs so no O(n^2) work happens inside recharts'
// per-cell shape callback.
const buildChartModel = (dynamicData) => {
  if (!dynamicData || dynamicData.length === 0) {
    return {
      mode: 'empty',
      transformedData: [],
      seriesKeys: [],
      topKeyByRow: new Map(),
    };
  }

  // SINGLE merchant -> X-axis is that merchant's stations. One <Bar> with a
  // <Cell> per station (bounded to the top MAX_STATION_SERIES + "Others").
  if (dynamicData.length === 1) {
    const stations = [...(dynamicData[0].stations || [])].sort(
      (a, b) => b.revenue - a.revenue,
    );
    const top = stations.slice(0, MAX_STATION_SERIES);
    const rest = stations.slice(MAX_STATION_SERIES);

    const rows = top.map((station) => ({
      name: String(station.stationId),
      value: station.revenue,
      count: station.count,
    }));

    if (rest.length) {
      rows.push({
        name: OTHERS_LABEL,
        value: sumBy(rest, (s) => s.revenue),
        count: sumBy(rest, (s) => s.count),
        isOthers: true,
      });
    }

    return {
      mode: 'single',
      transformedData: rows,
      seriesKeys: ['value'],
      topKeyByRow: new Map(),
    };
  }

  // MULTIPLE merchants -> X-axis is merchants (top MAX_MERCHANT_BARS by revenue),
  // stacked by station (top MAX_STATION_SERIES by revenue + an "Others" segment).
  const merchants = [...dynamicData]
    .sort(
      (a, b) =>
        sumBy(b.stations, (s) => s.revenue) -
        sumBy(a.stations, (s) => s.revenue),
    )
    .slice(0, MAX_MERCHANT_BARS);

  const stationRevenue = new Map();
  merchants.forEach((merchant) => {
    merchant.stations.forEach((station) => {
      const id = String(station.stationId);
      stationRevenue.set(
        id,
        (stationRevenue.get(id) || 0) + (station.revenue || 0),
      );
    });
  });

  const topStationIds = [...stationRevenue.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, MAX_STATION_SERIES)
    .map(([id]) => id);
  const topStationSet = new Set(topStationIds);

  let hasOthers = false;
  const transformedData = merchants.map((merchant) => {
    const row = { name: merchant.merchant };
    let othersRevenue = 0;
    let othersCount = 0;

    merchant.stations.forEach((station) => {
      const id = String(station.stationId);
      if (topStationSet.has(id)) {
        row[id] = station.revenue;
        row[`${id}-count`] = station.count;
      } else {
        othersRevenue += station.revenue || 0;
        othersCount += station.count || 0;
      }
    });

    if (othersRevenue > 0) {
      hasOthers = true;
      row[OTHERS_KEY] = othersRevenue;
      row[`${OTHERS_KEY}-count`] = othersCount;
    }
    return row;
  });

  const seriesKeys = hasOthers ? [...topStationIds, OTHERS_KEY] : topStationIds;

  // Precompute the top (last positive, in stack order) series per row so the
  // rounded-corner shape callback is O(1) instead of scanning every station.
  const topKeyByRow = new Map();
  transformedData.forEach((row) => {
    let topKey = null;
    seriesKeys.forEach((key) => {
      if ((row[key] || 0) > 0) topKey = key;
    });
    topKeyByRow.set(row.name, topKey);
  });

  return { mode: 'multi', transformedData, seriesKeys, topKeyByRow };
};

// Rounded top for the topmost stacked segment, plain rect otherwise.
const roundedTopPath = (x, y, width, height, fill) => {
  const radius = 5;
  const right = x + width;
  const bottom = y + height;
  return (
    <path
      d={`M ${x},${bottom} L ${x},${y + radius} Q ${x},${y} ${x + radius},${y} L ${right - radius},${y} Q ${right},${y} ${right},${y + radius} L ${right},${bottom} Z`}
      fill={fill}
    />
  );
};

// Defined outside the component so the reference is stable — prevents recharts
// from unmounting/remounting the tooltip on every render.
const StationTooltip = ({ active, payload, label, mode, currency }) => {
  if (!active || !payload?.length) return null;

  if (mode === 'single') {
    const row = payload[0].payload;
    return (
      <Box
        bg="white"
        p={3}
        border="1px solid #ddd"
        borderRadius="md"
        boxShadow="md"
      >
        <Text fontWeight="bold" mb={2}>
          {row.isOthers ? OTHERS_LABEL : `Station ${row.name}`}
        </Text>
        <Text fontSize="sm">
          <strong>Revenue:</strong> {formatCurrency(row.value, currency)}
        </Text>
        <Text fontSize="sm">
          <strong>Count:</strong> {row.count}
        </Text>
      </Box>
    );
  }

  return (
    <Box
      bg="white"
      p={3}
      border="1px solid #ddd"
      borderRadius="md"
      boxShadow="md"
    >
      <Text fontWeight="bold" mb={2}>
        {label}
      </Text>
      {payload.map((entry, index) => {
        const key = entry.dataKey;
        const isOthers = key === OTHERS_KEY;
        const count = entry.payload[`${key}-count`];
        return (
          <Box key={index} fontSize="sm" mb={2}>
            <Text>
              <strong>{isOthers ? OTHERS_LABEL : 'Station ID'}:</strong>{' '}
              {isOthers ? '' : key}
            </Text>
            <Text>
              <strong>Revenue:</strong> {formatCurrency(entry.value, currency)}
            </Text>
            <Text>
              <strong>Count:</strong> {count}
            </Text>
          </Box>
        );
      })}
    </Box>
  );
};

const MerchantRevenueStationGraph = ({
  dynamicData,
  isRecurringLoading,
  currency,
}) => {
  const { mode, transformedData, seriesKeys, topKeyByRow } = useMemo(
    () => buildChartModel(dynamicData),
    [dynamicData],
  );

  if (isRecurringLoading) {
    return (
      <Flex justify="center" align="center" height="300px" width="100%">
        <Spinner size="lg" color="black.500" />
      </Flex>
    );
  }

  if (mode === 'empty') {
    return (
      <Flex justify="center" alignItems="center" height="300px">
        <Text fontSize="18px" fontWeight="bold" color="gray.500">
          No records available
        </Text>
      </Flex>
    );
  }

  const barBottomMargin = transformedData.length < 4 ? -60 : 0;

  return (
    <Box width="100%" overflowX="auto">
      <Box
        minWidth={`${Math.max(transformedData.length * 50, 600)}px`}
        height={300}
      >
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={transformedData}
            margin={{ top: 20, right: 30, left: 20, bottom: barBottomMargin }}
            barCategoryGap={transformedData.length < 5 ? '10%' : '20%'}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              horizontal={true}
              vertical={false}
            />
            <XAxis
              dataKey="name"
              interval={0}
              tick={(props) => {
                const { x, y, payload } = props;
                const name = payload.value;
                const maxLength = 15;
                const truncatedName =
                  transformedData.length > 4
                    ? `${name.substring(0, maxLength)}…`
                    : name;

                return (
                  <text
                    x={x}
                    y={y + 10}
                    textAnchor={transformedData.length < 4 ? 'middle' : 'end'}
                    fontSize={12}
                    transform={
                      transformedData.length < 4
                        ? undefined
                        : `rotate(-60, ${x}, ${y})`
                    }
                  >
                    {truncatedName}
                  </text>
                );
              }}
              height={120}
            />
            <YAxis
              tickFormatter={(value) => `${formatCurrency(value, currency)}`}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip
              content={<StationTooltip mode={mode} currency={currency} />}
            />

            {mode === 'single' ? (
              <Bar dataKey="value" radius={[5, 5, 0, 0]}>
                {transformedData.map((entry, index) => (
                  <Cell key={entry.name} fill={colorAt(index)} />
                ))}
              </Bar>
            ) : (
              seriesKeys.map((key, index) => {
                const fill = colorAt(index);
                return (
                  <Bar
                    key={key}
                    dataKey={key}
                    stackId="a"
                    fill={fill}
                    name={key === OTHERS_KEY ? OTHERS_LABEL : key}
                    shape={(props) => {
                      const { x, y, width, height, payload } = props;
                      if (!height || height === 0) return null;
                      if (topKeyByRow.get(payload.name) === key) {
                        return roundedTopPath(x, y, width, height, fill);
                      }
                      return (
                        <rect
                          x={x}
                          y={y}
                          width={width}
                          height={height}
                          fill={fill}
                        />
                      );
                    }}
                  />
                );
              })
            )}
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};

export default React.memo(MerchantRevenueStationGraph);
